import { get } from "../modules/env";
import { fetchWithRetry, BpNetworkError } from "./network";

import type { GraphQLError } from "graphql";

export interface ApiClientQueryInit {
  query: string;
  variables?: Record<string, unknown>;
}

export interface ApiClientResponse<T> extends Record<string, unknown> {
  data: T;
  errors?: GraphQLError[];
}

/**
 * Enumeration of API client error reasons for API-level failures.
 */
export const ApiClientErrorReason = {
  ServerError: "SERVER_ERROR",
  ClientError: "CLIENT_ERROR",
  JsonParseError: "JSON_PARSE_ERROR",
  MissingUrl: "MISSING_URL",
} as const;

export type ApiClientErrorReason =
  (typeof ApiClientErrorReason)[keyof typeof ApiClientErrorReason];

/**
 * Error class for API client-level failures.
 */
export class BpApiClientError extends BpNetworkError<ApiClientErrorReason> {
  constructor(
    message: string,
    reason: ApiClientErrorReason,
    detail?: Record<string, unknown>,
  ) {
    super(message, reason, detail);
    this.name = "BpApiClientError";
  }
}

/**
 * Pared-down version of Apollo's gql tag to enable
 * formatting and highlighting. The request body is
 * converted to a DocumentNode by the receiving client.
 */
export function gql(chunks: TemplateStringsArray): string {
  return chunks.reduce((accumulator, chunk) => `${accumulator}${chunk}`, "");
}

/**
 * Parses a response as JSON, but throws an error if the
 * response is not valid JSON.
 */
async function safeParseResponse<T extends Record<string, unknown>>(
  response: Response,
): Promise<T> {
  try {
    return await response.json();
  } catch {
    throw new BpApiClientError(
      `Invalid JSON response from "${response.url}"`,
      ApiClientErrorReason.JsonParseError,
    );
  }
}

/**
 * Custom fetch implementation with retry logic.
 */
export async function apiFetch<T extends Record<string, unknown>>(
  init: RequestInit = {},
) {
  const url = get("API_URL");

  if (!url) {
    throw new BpApiClientError(
      "API URL missing",
      ApiClientErrorReason.MissingUrl,
    );
  }

  const headers = new Headers(init.headers);

  headers.set("Content-Type", "application/json");
  headers.set("X-BibleProject-Language", get("LANGUAGE") ?? "en");

  const response = await fetchWithRetry(url, {
    ...init,
    mode: "cors",
    headers,
  });

  // Parse JSON response with error handling
  const result = await safeParseResponse<T>(response);

  // Check response status and throw appropriate error
  if (!response.ok) {
    const reason =
      response.status >= 500
        ? ApiClientErrorReason.ServerError
        : ApiClientErrorReason.ClientError;

    throw new BpApiClientError(
      `Application error code ${response.status} for "${response.url}"`,
      reason,
      result,
    );
  }

  return result as T;
}

/**
 * Sends a GraphQL request to the API.
 */
export async function query<T = unknown>(
  options: ApiClientQueryInit,
  init?: Pick<RequestInit, "headers">,
): Promise<ApiClientResponse<T>> {
  return apiFetch<ApiClientResponse<T>>({
    method: "POST",
    body: JSON.stringify(options),
    ...init,
  });
}
